import React, { useState, useEffect, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { useMutate } from 'restful-react'
import {
    UserCommunityProfile,
    UserContext,
    UserContextType,
    UserProfile,
    createUserProfileFromApiUserDocument,
} from '../../UserContext'
import useInput, { Conditions } from '../../services/useInput'
import { unstable_batchedUpdates } from 'react-dom'
import * as Constants from '../../services/Constants'
import { logger } from '../../services/Logger'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { env } from '../../services/EnvironmentVariables'
import { serializeError } from 'serialize-error'
import { MuseLogo } from '@/components/MuseLogo'
import { TextInput } from '@/UIComponents2/TextInput'
import MailIcon from '@/CustomIcons/MailIcon'
import KeyIcon from '@/CustomIcons/KeyIcon'
import { Button } from '@/UIComponents2/Button'
import { motion } from 'framer-motion'
import RightArrowIcon from '@/CustomIcons/RightArrowIcon'
import { Alert } from '@/UIComponents2/Alert'
import UnauthorizedPageBase from './UnauthorizedPageBase'
import { PasswordInput } from '@/UIComponents2/PasswordInput'
import { LanguagesPopover } from '@/components/LanguagesPopover'
import { CircleIconButton } from '@/UIComponents2/CircleIconButton'
import LanguageIcon from '@/CustomIcons/LanguageIcon'

const log = logger.createChildLogger({ name: 'LoginPage' })

function LoginPage() {
    const history = useHistory()
    const { dispatch } = useContext(UserContext) as UserContextType
    const { t } = useTranslation()

    const { mutate: postLoginForm, loading: loginLoading } = useMutate({
        verb: 'POST',
        path: `/v1/login`,
    })

    const [errorText, setErrorText] = useState<string | null>(null)
    const [showError, setShowError] = useState<boolean>(false)
    const [clearPasswordEvent, setClearPasswordEvent] = useState<number>(0)

    const signinHandler = async (formdata: any) => {
        try {
            const result = await postLoginForm(formdata)
            if (result.token) {
                const profile: UserProfile = createUserProfileFromApiUserDocument(result.user)

                signinSuccessHandler(result.token, result.user?._id, profile, result.user.profile)
            } else {
                unstable_batchedUpdates(() => {
                    setErrorText('Internal error: the sever did not return a token.')
                    setShowError(true)
                })
            }
        } catch (e) {
            log.error('signinHandler', 'error=' + JSON.stringify(serializeError(e)))
            const response = (e as any) || {}
            if (response?.data?.code === 'unauthorized') {
                unstable_batchedUpdates(() => {
                    setErrorText("We couldn't log you in! Your credentials do not match.")
                    setShowError(true)
                    setClearPasswordEvent((n) => n + 1)
                })
            } else if (!response?.data) {
                unstable_batchedUpdates(() => {
                    setErrorText("Couldn't connect to the server. Please make sure your are connected to the internet.")
                    setShowError(true)
                })
            } else {
                unstable_batchedUpdates(() => {
                    setErrorText(`Unknown error: status=${(e as any)?.status} message=${(e as any)?.message}`)
                    setShowError(true)
                })
            }
        }
    }

    const signinSuccessHandler = (
        authToken: string,
        userUrn: string,
        profile: UserProfile,
        communityProfile: UserCommunityProfile
    ) => {
        console.log(`signinSuccessHandler: userUrn=${userUrn} profile=${JSON.stringify(profile)}`)
        dispatch({
            type: 'login',
            value: { authToken, userUrn, profile, communityProfile, isGuest: false },
        })
        history.push('/')
    }

    useEffect(() => {
        const url = new URL(window.location.toString())
        const code = url.searchParams.get('code')
        if (!env.IS_ELECTRON && code) {
            history.push(`/guest-join${url.search}`)
            return
        }

        logger.event({ page: 'login', action: 'enter' })
        return () => {
            logger.event({ page: 'log', action: 'exit' })
        }
    }, [])

    return (
        <UnauthorizedPageBase loading={loginLoading}>
            <div className="flex justify-between items-center">
                <div className="w-[108px] h-[24px]">
                    <MuseLogo />
                </div>
                <LanguagesPopover>
                    <CircleIconButton>
                        <LanguageIcon />
                    </CircleIconButton>
                </LanguagesPopover>
            </div>
            <motion.div
                key="login-form"
                className="flex flex-col gap-[38px] justify-center h-[80%]"
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -50, opacity: 0 }}
                transition={{
                    duration: 0.3,
                    ease: 'easeOut',
                }}
            >
                <div className="flex flex-col gap-[14px]">
                    <span className="max-w-[442px] text-[30px]/[30px] font-[500] bg-linear-[98deg,white_21.88%,transparent_139.38%] bg-clip-text text-transparent">
                        {t('Welcome to Muse')}
                    </span>
                    <span className="w-auto text-[16px] font-[400] text-[rgba(255,255,255,0.75)]">
                        {t('Please Sign In or Sign Up below')}
                    </span>
                </div>
                <FormContent
                    onSubmit={signinHandler}
                    clearPasswordEvent={clearPasswordEvent}
                    loginLoading={loginLoading}
                    showError={showError}
                    errorText={errorText}
                    setShowError={setShowError}
                />
            </motion.div>
        </UnauthorizedPageBase>
    )
}

function FormContent({
    onSubmit,
    clearPasswordEvent,
    loginLoading,
    showError,
    errorText,
    setShowError,
}: {
    onSubmit: (formdata: any) => void
    clearPasswordEvent: number
    loginLoading: boolean
    showError: boolean
    errorText: string | null
    setShowError: (showError: boolean) => void
}) {
    const history = useHistory()

    const { t } = useTranslation()

    const { formdata, bind, submit, setValue } = useInput(inputModel)
    const [prevClearPasswordEvent, setPrevClearPasswordEvent] = useState<number>(0)
    if (prevClearPasswordEvent !== clearPasswordEvent) {
        setPrevClearPasswordEvent(clearPasswordEvent)
        setValue('password', '')
    }

    const onSubmitHandler = async (e: React.FormEvent) => {
        e.preventDefault()
        setShowError(false)

        const { hasErrors } = await submit()
        if (!hasErrors) {
            formdata.clientKind = Constants.clientKind
            onSubmit(formdata)
        }
    }

    const [showPassword, setShowPassword] = useState(false)

    return (
        <>
            <form
                noValidate
                onSubmit={onSubmitHandler}
                onKeyDown={(ev) => {
                    if (ev.key === 'Enter') onSubmitHandler(ev)
                }}
                className="flex flex-col gap-[12px]"
            >
                {showError && (
                    <Alert variant="solid" level="danger">
                        {errorText}
                    </Alert>
                )}
                <TextInput
                    placeholder={t('Email / Username') || ''}
                    {...bind.email}
                    error={bind.email.error ? bind.email.helperText : undefined}
                    helperText={undefined}
                    className="bg-[rgba(255,255,255,0.08)]"
                    icon={<MailIcon />}
                    type="email"
                />
                <div>
                    <PasswordInput
                        placeholder={t('Password') || ''}
                        {...bind.password}
                        error={bind.password.error ? bind.password.helperText : undefined}
                        helperText={undefined}
                        className="bg-[rgba(255,255,255,0.08)]"
                        icon={<KeyIcon />}
                    />
                    <a
                        onClick={(e) => {
                            e.preventDefault()
                            history.push('/reset-password')
                        }}
                        className="text-[rgba(252,230,64,0.75)] float-right pt-[8px] cursor-pointer"
                    >
                        {t('Forgot Password?')}
                    </a>
                </div>
                <Button variant="primary" className="w-full relative" disabled={loginLoading} onClick={onSubmitHandler}>
                    {env.JOIN_ROOM_LINK ? t('Join Call') : t('Login')}
                    <RightArrowIcon className="absolute right-4" />
                </Button>
                <Button
                    variant="secondary"
                    className="w-full text-muse-fg/75"
                    onClick={(e) => {
                        e.preventDefault()
                        history.push('/register')
                    }}
                >
                    {t('Not a member?')} <span className="text-[#FCE640] pl-[8px]">{t('Sign up with Email')}</span>
                </Button>
            </form>
        </>
    )
}

const inputModel: any = {
    email: {
        errorChecks: [
            {
                trigger: ['onSubmit'],
                condition: Conditions.is_empty,
                message: () => i18n.t('Email or username is required'),
            },
        ],
    },
    password: {
        errorChecks: [
            {
                trigger: ['onSubmit'],
                condition: Conditions.is_empty,
                message: () => i18n.t('Password is required'),
            },
        ],
    },
}

export default LoginPage
